import { useState, useCallback, useMemo } from "react";
import {
  Create,
  SimpleForm,
  TextInput,
  required,
  useNotify,
  useRedirect,
  SaveButton,
} from "react-admin";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Alert,
  Paper,
  Chip,
  Typography,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { RecipientSelectionStep, TemplateSelectionStep, SummaryStep } from "./components";
import BroadcastModeFields from "./components/BroadcastModeFields";
import { TemplateProvider, RecipientSelectionProvider, useRecipientSelection } from "./context";
import {
  transformBroadcastData,
  validateBroadcastCreateForm,
  type BroadcastCreateFormValues,
  DEFAULT_FMB_OUTSTANDING_MIN_PERCENT,
  DEFAULT_FMB_OUTSTANDING_TEMPLATE_NAME,
  FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_AND_FAMILY,
} from "./components/utils";
import { parseFmbOutstandingBroadcastDeepLink } from "@/utils/fmb-outstanding-broadcast";
import { useIsFmbOutstandingBroadcastEnabled } from "@/hooks/useIsFmbOutstandingBroadcastEnabled";

type BroadcastFormInnerProps = {
  activeStep: number;
  stepValidations: { step0: boolean; step1: boolean };
  stepErrors: { step0: string | null; step1: string | null };
  handleNext: () => void;
  handleBack: () => void;
  handleResetToStep0: () => void;
  handleStep0ValidationChange: (isValid: boolean, errorMessage: string | null) => void;
  handleStepValidationChange: (
    stepIndex: number,
    isValid: boolean,
    errorMessage?: string | null
  ) => void;
  handleMiqaatModeTemplateStep: (isValid: boolean, errorMessage?: string | null) => void;
  handleEventModeTemplateStep: (isValid: boolean, errorMessage?: string | null) => void;
  handleFmbOutstandingModeTemplateStep: (isValid: boolean, errorMessage?: string | null) => void;
  handleSabilOutstandingModeTemplateStep: (isValid: boolean, errorMessage?: string | null) => void;
  handleMuttavatteenOutstandingModeTemplateStep: (
    isValid: boolean,
    errorMessage?: string | null
  ) => void;
};

function BroadcastFormInner({
  activeStep,
  stepValidations,
  stepErrors,
  handleNext,
  handleBack,
  handleResetToStep0,
  handleStep0ValidationChange,
  handleStepValidationChange,
  handleMiqaatModeTemplateStep,
  handleEventModeTemplateStep,
  handleFmbOutstandingModeTemplateStep,
  handleSabilOutstandingModeTemplateStep,
  handleMuttavatteenOutstandingModeTemplateStep,
}: BroadcastFormInnerProps) {
  const { recipientCount } = useRecipientSelection();

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      {/* Broadcast Name & Mode Selector */}
      <Box sx={{ mb: 3, minWidth: 0, width: "100%" }}>
        <TextInput
          source="name"
          label="Broadcast Name"
          validate={required("Broadcast name is required")}
          helperText="A descriptive name for this broadcast"
          isRequired
          fullWidth
          sx={{ maxWidth: { xs: "100%", sm: 560 } }}
        />
        <BroadcastModeFields
          activeStep={activeStep}
          onResetToStep0={handleResetToStep0}
          onMiqaatModeTemplateStep={handleMiqaatModeTemplateStep}
          onEventModeTemplateStep={handleEventModeTemplateStep}
          onFmbOutstandingModeTemplateStep={handleFmbOutstandingModeTemplateStep}
          onSabilOutstandingModeTemplateStep={handleSabilOutstandingModeTemplateStep}
          onMuttavatteenOutstandingModeTemplateStep={handleMuttavatteenOutstandingModeTemplateStep}
        />
      </Box>

      {/* Horizontal Stepper */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: 3,
          width: "100%",
          minWidth: 0,
          borderRadius: 2,
          bgcolor: "background.paper",
        }}
      >
        <Stepper
          activeStep={activeStep}
          orientation="horizontal"
          alternativeLabel
          sx={{
            width: "100%",
            "& .MuiStepLabel-label": {
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              fontWeight: 600,
            },
          }}
        >
          <Step>
            <StepLabel>1. Recipients & Audience</StepLabel>
          </Step>
          <Step>
            <StepLabel>2. Template & Message</StepLabel>
          </Step>
          <Step>
            <StepLabel>3. Review & Confirmation</StepLabel>
          </Step>
        </Stepper>
      </Paper>

      {/* Full-width Step Content Container */}
      <Box sx={{ width: "100%", minWidth: 0, mb: 4 }}>
        {activeStep === 0 && (
          <Box sx={{ width: "100%", minWidth: 0 }}>
            <RecipientSelectionStep onValidationChange={handleStep0ValidationChange} />
            {stepErrors.step0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {stepErrors.step0}
              </Alert>
            )}
          </Box>
        )}

        {activeStep === 1 && (
          <Box sx={{ width: "100%", minWidth: 0 }}>
            <TemplateSelectionStep
              onValidationChange={(isValid, errorMessage) =>
                handleStepValidationChange(1, isValid, errorMessage)
              }
            />
            {stepErrors.step1 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {stepErrors.step1}
              </Alert>
            )}
          </Box>
        )}

        {activeStep === 2 && (
          <Box sx={{ width: "100%", minWidth: 0 }}>
            <SummaryStep />
          </Box>
        )}
      </Box>

      {/* Sticky Bottom Summary & Navigation Bar */}
      <Paper
        elevation={4}
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 10,
          px: { xs: 1.5, sm: 3 },
          py: 1.5,
          mt: 3,
          borderRadius: "8px 8px 0 0",
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Chip
            label={`${recipientCount} recipient${recipientCount !== 1 ? "s" : ""}`}
            color={recipientCount > 0 ? "primary" : "default"}
            variant={recipientCount > 0 ? "filled" : "outlined"}
            size="small"
            sx={{ fontWeight: 600 }}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: { xs: "none", sm: "inline" } }}
          >
            Step {activeStep + 1} of 3
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: "auto" }}>
          {activeStep > 0 && (
            <Button onClick={handleBack} variant="outlined" size="medium">
              Back
            </Button>
          )}
          {activeStep < 2 && (
            <Button
              variant="contained"
              onClick={handleNext}
              size="medium"
              disabled={activeStep === 0 ? !stepValidations.step0 : !stepValidations.step1}
            >
              Next
            </Button>
          )}
          {activeStep === 2 && <SaveButton label="Create Broadcast" />}
        </Box>
      </Paper>
    </Box>
  );
}

export default function WhatsappBroadcastCreate() {
  const notify = useNotify();
  const redirect = useRedirect();
  const [searchParams] = useSearchParams();
  const fmbOutstandingEnabled = useIsFmbOutstandingBroadcastEnabled();
  const deepLinkDefaults = useMemo(() => {
    if (!fmbOutstandingEnabled) return null;
    return parseFmbOutstandingBroadcastDeepLink(searchParams);
  }, [fmbOutstandingEnabled, searchParams]);

  const [activeStep, setActiveStep] = useState(0);
  const [stepValidations, setStepValidations] = useState({
    step0: false,
    step1: false,
  });
  const [stepErrors, setStepErrors] = useState<{
    step0: string | null;
    step1: string | null;
  }>({
    step0: null,
    step1: null,
  });

  const transform = useCallback(
    (data: BroadcastCreateFormValues) => transformBroadcastData(data),
    []
  );

  const onSuccess = useCallback(
    (data: { id: string | number }) => {
      notify("Broadcast created successfully. Messages are being sent.", {
        type: "success",
      });
      redirect("show", "whatsappBroadcasts", data.id);
    },
    [notify, redirect]
  );

  const handleStepValidationChange = useCallback(
    (stepIndex: number, isValid: boolean, errorMessage: string | null = null) => {
      setStepValidations((prev) => ({
        ...prev,
        [`step${stepIndex}`]: isValid,
      }));
      setStepErrors((prev) => ({
        ...prev,
        [`step${stepIndex}`]: errorMessage,
      }));
    },
    []
  );

  const handleNext = useCallback(() => {
    const currentStepKey = `step${activeStep}` as "step0" | "step1";
    const isCurrentStepValid = stepValidations[currentStepKey];

    if (!isCurrentStepValid) {
      const errorMessage = stepErrors[currentStepKey];
      if (errorMessage) {
        notify(errorMessage, { type: "warning" });
      } else if (activeStep === 0) {
        notify(
          "Please preview recipients before proceeding. Click 'Preview Recipients' for filter-based selection.",
          { type: "warning" }
        );
      } else if (activeStep === 1) {
        notify("Please select a template and fill all required parameters before proceeding.", {
          type: "warning",
        });
      }
      return;
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setStepErrors((prev) => ({
      ...prev,
      [currentStepKey]: null,
    }));
  }, [activeStep, stepValidations, stepErrors, notify]);

  const handleBack = useCallback(() => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  }, []);

  const handleMiqaatModeTemplateStep = useCallback(
    (isValid: boolean, errorMessage: string | null = null) => {
      handleStepValidationChange(1, isValid, errorMessage);
    },
    [handleStepValidationChange]
  );

  const handleEventModeTemplateStep = useCallback(
    (isValid: boolean, errorMessage: string | null = null) => {
      handleStepValidationChange(1, isValid, errorMessage);
    },
    [handleStepValidationChange]
  );

  const handleFmbOutstandingModeTemplateStep = useCallback(
    (isValid: boolean, errorMessage: string | null = null) => {
      handleStepValidationChange(1, isValid, errorMessage);
    },
    [handleStepValidationChange]
  );

  const handleSabilOutstandingModeTemplateStep = useCallback(
    (isValid: boolean, errorMessage: string | null = null) => {
      handleStepValidationChange(1, isValid, errorMessage);
    },
    [handleStepValidationChange]
  );

  const handleMuttavatteenOutstandingModeTemplateStep = useCallback(
    (isValid: boolean, errorMessage: string | null = null) => {
      handleStepValidationChange(1, isValid, errorMessage);
    },
    [handleStepValidationChange]
  );

  const handleStep0ValidationChange = useCallback(
    (isValid: boolean, errorMessage: string | null) =>
      handleStepValidationChange(0, isValid, errorMessage),
    [handleStepValidationChange]
  );

  const handleResetToStep0 = useCallback(() => {
    setActiveStep(0);
    setStepValidations({ step0: false, step1: false });
    setStepErrors({ step0: null, step1: null });
  }, []);

  const formDefaultValues = useMemo(
    () => ({
      broadcastKind: deepLinkDefaults?.broadcastKind ?? "TEMPLATE",
      parameters: {},
      minOutstandingPercent:
        deepLinkDefaults?.minOutstandingPercent ?? DEFAULT_FMB_OUTSTANDING_MIN_PERCENT,
      notifyMode: deepLinkDefaults?.notifyMode ?? FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_AND_FAMILY,
      thaliTypeIds: deepLinkDefaults?.thaliTypeIds ?? [],
      deliveryScheduleProfileIds: deepLinkDefaults?.deliveryScheduleProfileIds ?? [],
      itsIds: deepLinkDefaults?.itsIds ?? [],
      ...(deepLinkDefaults?.hijriYearStart != null
        ? { hijriYearStart: deepLinkDefaults.hijriYearStart }
        : {}),
      ...(deepLinkDefaults?.broadcastKind === "FMB_OUTSTANDING"
        ? { templateName: DEFAULT_FMB_OUTSTANDING_TEMPLATE_NAME }
        : {}),
    }),
    [deepLinkDefaults]
  );

  return (
    <Create transform={transform} mutationOptions={{ onSuccess }} title="Create WhatsApp Broadcast">
      <SimpleForm
        validate={validateBroadcastCreateForm}
        toolbar={false}
        defaultValues={formDefaultValues}
      >
        <TemplateProvider>
          <RecipientSelectionProvider>
            <BroadcastFormInner
              activeStep={activeStep}
              stepValidations={stepValidations}
              stepErrors={stepErrors}
              handleNext={handleNext}
              handleBack={handleBack}
              handleResetToStep0={handleResetToStep0}
              handleStep0ValidationChange={handleStep0ValidationChange}
              handleStepValidationChange={handleStepValidationChange}
              handleMiqaatModeTemplateStep={handleMiqaatModeTemplateStep}
              handleEventModeTemplateStep={handleEventModeTemplateStep}
              handleFmbOutstandingModeTemplateStep={handleFmbOutstandingModeTemplateStep}
              handleSabilOutstandingModeTemplateStep={handleSabilOutstandingModeTemplateStep}
              handleMuttavatteenOutstandingModeTemplateStep={
                handleMuttavatteenOutstandingModeTemplateStep
              }
            />
          </RecipientSelectionProvider>
        </TemplateProvider>
      </SimpleForm>
    </Create>
  );
}
